import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from "../../config/api";

interface Category {
  id: string;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  color?: string;
}

interface Instruction {
  category_id: string;
  category_name: string;
  description: string;
  systemPrompt: string;
  allowedTopics: string[];
  restrictedKeywords: string[];
  tone: string;
  style: string;
  categoryId?: string;
  categoryName?: string;
  fallbackResponses: {
    offTopic?: string;
    generalInquiry?: string;
    missingProductInfo?: string;
    outOfScope?: string;
  };
  isActive: boolean;
  lastModified: string;
  modifiedBy: string;
  topicsCount?: number;
  restrictedCount?: number;
}

const AdminInstructions: React.FC = () => {
  const navigate = useNavigate();
  const [instructions, setInstructions] = useState<Instruction[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedInstruction, setSelectedInstruction] = useState<Instruction | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState("");

  const [formData, setFormData] = useState<Partial<Instruction>>({
    category_name: "",
    description: "",
    systemPrompt: "",
    allowedTopics: [],
    restrictedKeywords: [],
    tone: "professional-friendly",
    style: "helpful-general",
    categoryId: "",
    fallbackResponses: {
      offTopic: "",
      generalInquiry: "",
      missingProductInfo: "",
      outOfScope: "",
    },
    isActive: true,
  });

  // Available tone options
  const toneOptions = [
    { value: "professional-friendly", label: "Professional Friendly" },
    { value: "professional-warm", label: "Professional Warm" },
    { value: "technical-professional", label: "Technical Professional" },
    { value: "professional-thorough", label: "Professional Thorough" },
    { value: "casual-friendly", label: "Casual Friendly" },
    { value: "formal-professional", label: "Formal Professional" },
  ];

  // Available style options
  const styleOptions = [
    { value: "helpful-general", label: "Helpful General" },
    { value: "helpful-sales-focused", label: "Helpful Sales Focused" },
    { value: "consultative-trustworthy", label: "Consultative Trustworthy" },
    { value: "methodical-precise", label: "Methodical Precise" },
    { value: "detail-oriented-cautious", label: "Detail Oriented Cautious" },
    { value: "creative-innovative", label: "Creative Innovative" },
    { value: "analytical-thorough", label: "Analytical Thorough" },
  ];

  useEffect(() => {
    fetchInstructions();
    fetchCategories();
  }, []);

  // Auto-fill category_name when categoryId changes
  useEffect(() => {
    if (formData.categoryId && categories.length > 0) {
      const selectedCategory = categories.find(cat => cat.id === formData.categoryId);
      if (selectedCategory) {
        setFormData(prev => ({
          ...prev,
          category_name: selectedCategory.name
        }));
      }
    }
  }, [formData.categoryId, categories]);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(`${API_BASE_URL}/admin/categories/select`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setCategories(data.data);
      } else {
        console.error("Failed to fetch categories:", data.error);
      }
    } catch (err: any) {
      console.error("Fetch categories error:", err);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchInstructions = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(`${API_BASE_URL}/admin/instructions`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setInstructions(data.data);
        setError("");
      } else {
        setError(data.error || "Failed to fetch instructions");
      }
    } catch (err: any) {
      console.error("Fetch instructions error:", err);
      setError(err.message || "Network error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectInstruction = async (categoryId: string) => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${API_BASE_URL}/admin/instructions/${categoryId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setSelectedInstruction(data.data);
        setFormData(data.data);
        setIsEditing(false);
        setError("");
      } else {
        setError(data.error || "Failed to load instruction details");
      }
    } catch (err: any) {
      console.error("Select instruction error:", err);
      setError(err.message || "Failed to load instruction details");
    }
  };

  const handleCreateNew = () => {
    setSelectedInstruction(null);
    setFormData({
      category_name: "",
      description: "",
      systemPrompt: "",
      allowedTopics: [],
      restrictedKeywords: [],
      tone: "professional-friendly",
      style: "helpful-general",
      categoryId: "",
      fallbackResponses: {
        offTopic: "",
        generalInquiry: "",
        missingProductInfo: "",
        outOfScope: "",
      },
      isActive: true,
    });
    setIsCreating(true);
    setIsEditing(true);
  };

  // Helper function to generate category_id from category_name
  const generateCategoryId = (categoryName: string): string => {
    return categoryName
      .toUpperCase()
      .replace(/[^A-Z0-9\s]/g, '') // Remove special characters
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .replace(/_+/g, '_') // Replace multiple underscores with single
      .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
  };

  // Handle database category change and auto-fill category name
  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCategoryId = e.target.value;
    const selectedCategory = categories.find(cat => cat.id === selectedCategoryId);
    
    setFormData({
      ...formData,
      categoryId: selectedCategoryId,
      category_name: selectedCategory ? selectedCategory.name : ""
    });
  };

  const handleSave = async () => {
    try {
      // Basic validation
      if (!formData.categoryId?.trim()) {
        setError("Database category selection is required");
        return;
      }
      if (!formData.category_name?.trim()) {
        setError("Category name is required");
        return;
      }
      if (!formData.description?.trim()) {
        setError("Description is required");
        return;
      }
      if (!formData.systemPrompt?.trim()) {
        setError("System prompt is required");
        return;
      }

      const token = localStorage.getItem("adminToken");
      
      // Prepare submission data
      const submissionData = {
        ...formData,
        category_id: isCreating ? generateCategoryId(formData.category_name!) : selectedInstruction?.category_id,
      };

      const url = isCreating
        ? `${API_BASE_URL}/admin/instructions`
        : `${API_BASE_URL}/admin/instructions/${selectedInstruction?.category_id}`;

      const method = isCreating ? "POST" : "PUT";

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(submissionData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        await fetchInstructions();
        setIsEditing(false);
        setIsCreating(false);
        setSelectedInstruction(data.data);
        setError("");
        setSuccessMessage("Instruction saved successfully!");
        setTimeout(() => {
          setSuccessMessage("");
        }, 3000);
      } else {
        setError(
          data.error || data.details?.join(", ") || "Failed to save instruction"
        );
      }
    } catch (err: any) {
      console.error("Save instruction error:", err);
      setError(err.message || "Failed to save instruction");
    }
  };

  const handleToggleStatus = async (categoryId: string) => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${API_BASE_URL}/admin/instructions/${categoryId}/toggle`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        await fetchInstructions();
        if (selectedInstruction?.category_id === categoryId) {
          setSelectedInstruction(data.data);
        }
        setSuccessMessage(
          `Instruction ${
            data.data.isActive ? "activated" : "deactivated"
          } successfully!`
        );
        setTimeout(() => setSuccessMessage(""), 3000);
        setError("");
      } else {
        setError(data.error || "Failed to toggle instruction status");
      }
    } catch (err: any) {
      console.error("Toggle status error:", err);
      setError(err.message || "Failed to toggle instruction status");
    }
  };

  const handleDelete = async (categoryId: string) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this instruction? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${API_BASE_URL}/admin/instructions/${categoryId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        await fetchInstructions();
        if (selectedInstruction?.category_id === categoryId) {
          setSelectedInstruction(null);
        }
        setSuccessMessage("Instruction deleted successfully!");
        setTimeout(() => setSuccessMessage(""), 3000);
        setError("");
      } else {
        setError(data.error || "Failed to delete instruction");
      }
    } catch (err: any) {
      console.error("Delete instruction error:", err);
      setError(err.message || "Failed to delete instruction");
    }
  };

  const handleBackup = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${API_BASE_URL}/admin/instructions/backup`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setSuccessMessage("Backup created successfully!");
        setTimeout(() => setSuccessMessage(""), 3000);
        setError("");
      } else {
        setError(data.error || "Failed to create backup");
      }
    } catch (err: any) {
      console.error("Backup error:", err);
      setError(err.message || "Failed to create backup");
    }
  };

  const getCategoryName = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : categoryId;
  };

  const filteredInstructions = instructions.filter((instruction) => {
    const matchesSearch = 
      instruction.category_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      instruction.category_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getCategoryName(instruction.categoryId || "").toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = !selectedCategoryFilter || instruction.categoryId === selectedCategoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  if (loading || loadingCategories) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading instructions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                AI Agent Instructions
              </h1>
              <p className="text-gray-600">
                Create and manage AI agent behavior and responses
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBackup}
                className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Backup
              </button>
              <button
                onClick={handleCreateNew}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Create New
              </button>
              <button
                onClick={() => navigate("/admin/dashboard")}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error/Success Messages */}
      {(error || successMessage) && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-red-700">{error}</p>
                </div>
                <div className="ml-auto pl-3">
                  <button
                    onClick={() => setError("")}
                    className="inline-flex rounded-md bg-red-50 p-1.5 text-red-500 hover:bg-red-100"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
          {successMessage && (
            <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-green-700">{successMessage}</p>
                </div>
                <div className="ml-auto pl-3">
                  <button
                    onClick={() => setSuccessMessage("")}
                    className="inline-flex rounded-md bg-green-50 p-1.5 text-green-500 hover:bg-green-100"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Instructions List */}
          <div className="lg:col-span-1">
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Instructions ({filteredInstructions.length})
                </h3>

                {/* Search and Filter */}
                <div className="mb-4 space-y-2">
                  <input
                    type="text"
                    placeholder="Search instructions..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <select
                    value={selectedCategoryFilter}
                    onChange={(e) => setSelectedCategoryFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Categories</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  {filteredInstructions.map((instruction) => (
                    <div
                      key={instruction.category_id} 
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedInstruction?.category_id === instruction.category_id
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      onClick={() => handleSelectInstruction(instruction.category_id)}  
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">
                            {instruction.category_name}
                          </h4>
                          <p className="text-sm text-gray-500 mt-1">
                            {instruction.category_id}
                          </p>
                          {instruction.categoryId && (
                            <p className="text-xs text-blue-600 mt-1">
                              {getCategoryName(instruction.categoryId)}
                            </p>
                          )}
                          <div className="flex items-center mt-2 space-x-4">
                            <span className="text-xs text-gray-400">
                              Topics: {instruction.topicsCount || 0}
                            </span>
                            <span className="text-xs text-gray-400">
                              Restricted: {instruction.restrictedCount || 0}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              instruction.isActive
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {instruction.isActive ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}

                  {filteredInstructions.length === 0 && (
                    <div className="text-center py-8">
                      <div className="text-gray-400 mb-2">
                        <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <p className="text-gray-500 text-sm">No instructions found</p>
                      <button
                        onClick={handleCreateNew}
                        className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                      >
                        Create your first instruction
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Instruction Details */}
          <div className="lg:col-span-2">
            {selectedInstruction || isCreating ? (
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-medium text-gray-900">
                      {isCreating
                        ? "Create New Instruction"
                        : `${selectedInstruction?.category_name} Details`}
                    </h3>
                    <div className="flex items-center space-x-2">
                      {!isCreating && selectedInstruction && (
                        <>
                          <button
                            onClick={() =>
                              handleToggleStatus(selectedInstruction.category_id)
                            }
                            className={`px-3 py-1 rounded text-sm font-medium ${
                              selectedInstruction.isActive
                                ? "bg-red-100 text-red-800 hover:bg-red-200"
                                : "bg-green-100 text-green-800 hover:bg-green-200"
                            }`}
                          >
                            {selectedInstruction.isActive
                              ? "Deactivate"
                              : "Activate"}
                          </button>
                          <button
                            onClick={() => handleDelete(selectedInstruction.category_id)}
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm font-medium"
                          >
                            Delete
                          </button>
                        </>
                      )}
                      {isEditing ? (
                        <div className="flex space-x-2">
                          <button
                            onClick={handleSave}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setIsEditing(false);
                              setIsCreating(false);
                              if (selectedInstruction) {
                                setFormData(selectedInstruction);
                              }
                            }}
                            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setIsEditing(true)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  </div>

                  {isEditing ? (
                    <form onSubmit={(e) => e.preventDefault()}>
                      <div className="space-y-6">
                        {/* Basic Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Database Category *
                            </label>
                            <select
                              value={formData.categoryId || ""}
                              onChange={handleCategoryChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Select a category</option>
                              {categories.map((category) => (
                                <option key={category.id} value={category.id}>
                                  {category.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Category Name *
                            </label>
                            <input
                              type="text"
                              value={formData.category_name || ""}
                              readOnly
                              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed focus:outline-none"
                              placeholder="Will be auto-filled when you select a database category"
                            />
                            {formData.category_name && (
                              <p className="text-xs text-gray-500 mt-1">
                                Generated ID: {generateCategoryId(formData.category_name)}
                              </p>
                            )}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description *
                          </label>
                          <textarea
                            value={formData.description || ""}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                description: e.target.value,
                              })
                            }
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Brief description of what this AI agent specializes in..."
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            System Prompt *
                          </label>
                          <textarea
                            value={formData.systemPrompt || ""}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                systemPrompt: e.target.value,
                              })
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.stopPropagation();
                              }
                            }}
                            rows={12}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
                            placeholder="You are a specialized AI assistant. Your role is to..."
                            style={{ whiteSpace: "pre-wrap" }}
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Define how the AI agent should behave and respond. Be specific about limitations and scope.
                            Characters: {formData.systemPrompt?.length || 0}/4000
                          </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Tone
                            </label>
                            <select
                              value={formData.tone || ""}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  tone: e.target.value,
                                })
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              {toneOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Style
                            </label>
                            <select
                              value={formData.style || ""}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  style: e.target.value,
                                })
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              {styleOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Topics and Keywords */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Allowed Topics (one per line)
                            </label>
                            <textarea
                              value={formData.allowedTopics?.join("\n") || ""}
                              onChange={(e) => {
                                const topics = e.target.value.split("\n");
                                setFormData({
                                  ...formData,
                                  allowedTopics: topics,
                                });
                              }}
                              onBlur={(e) => {
                                setFormData({
                                  ...formData,
                                  allowedTopics: e.target.value
                                    .split("\n")
                                    .filter((t) => t.trim()),
                                });
                              }}
                              rows={8}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
                              placeholder="product&#10;inventory&#10;order&#10;shipping&#10;payment"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Enter topics the AI can discuss. Total topics: {formData.allowedTopics?.length || 0}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Restricted Keywords (one per line)
                            </label>
                            <textarea
                              value={formData.restrictedKeywords?.join("\n") || ""}
                              onChange={(e) => {
                                const keywords = e.target.value.split("\n");
                                setFormData({
                                  ...formData,
                                  restrictedKeywords: keywords,
                                });
                              }}
                              onBlur={(e) => {
                                setFormData({
                                  ...formData,
                                  restrictedKeywords: e.target.value
                                    .split("\n")
                                    .filter((k) => k.trim()),
                                });
                              }}
                              rows={8}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
                              placeholder="real estate&#10;medical advice&#10;legal advice&#10;personal finance"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Enter topics/keywords to avoid. Total keywords: {formData.restrictedKeywords?.length || 0}
                            </p>
                          </div>
                        </div>

                        {/* Fallback Responses */}
                        <div>
                          <h4 className="text-lg font-medium text-gray-900 mb-4">
                            Fallback Responses
                          </h4>
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Off-Topic Response
                              </label>
                              <textarea
                                value={formData.fallbackResponses?.offTopic || ""}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    fallbackResponses: {
                                      ...formData.fallbackResponses,
                                      offTopic: e.target.value,
                                    },
                                  })
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.stopPropagation();
                                  }
                                }}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
                                placeholder="Response when user asks off-topic questions..."
                                style={{ whiteSpace: "pre-wrap" }}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                General Inquiry Response
                              </label>
                              <textarea
                                value={formData.fallbackResponses?.generalInquiry || ""}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    fallbackResponses: {
                                      ...formData.fallbackResponses,
                                      generalInquiry: e.target.value,
                                    },
                                  })
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.stopPropagation();
                                  }
                                }}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
                                placeholder="Response for general questions and greetings..."
                                style={{ whiteSpace: "pre-wrap" }}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Missing Information Response
                              </label>
                              <textarea
                                value={formData.fallbackResponses?.missingProductInfo || ""}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    fallbackResponses: {
                                      ...formData.fallbackResponses,
                                      missingProductInfo: e.target.value,
                                    },
                                  })
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.stopPropagation();
                                  }
                                }}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
                                placeholder="Response when more information is needed..."
                                style={{ whiteSpace: "pre-wrap" }}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Out of Scope Response
                              </label>
                              <textarea
                                value={formData.fallbackResponses?.outOfScope || ""}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    fallbackResponses: {
                                      ...formData.fallbackResponses,
                                      outOfScope: e.target.value,
                                    },
                                  })
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.stopPropagation();
                                  }
                                }}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
                                placeholder="Response for requests outside the AI's scope..."
                                style={{ whiteSpace: "pre-wrap" }}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Active Status */}
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="isActive"
                            checked={formData.isActive || false}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                isActive: e.target.checked,
                              })
                            }
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label
                            htmlFor="isActive"
                            className="ml-2 block text-sm text-gray-900"
                          >
                            Active (Enable this instruction)
                          </label>
                        </div>
                      </div>
                    </form>
                  ) : (
                    <div className="space-y-6">
                      {/* View Mode */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wide">
                            Basic Information
                          </h4>
                          <dl className="mt-2 space-y-2">
                            <div>
                              <dt className="text-sm font-medium text-gray-500">
                                Category Name
                              </dt>
                              <dd className="text-sm text-gray-900">
                                {selectedInstruction?.category_name}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-sm font-medium text-gray-500">
                                Database Category ID
                              </dt>
                              <dd className="text-sm text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded">
                                {selectedInstruction?.categoryId || "Not assigned"}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-sm font-medium text-gray-500">
                                Database Category Name
                              </dt>
                              <dd className="text-sm text-gray-900">
                                {selectedInstruction?.categoryId 
                                  ? getCategoryName(selectedInstruction.categoryId)
                                  : "No category assigned"}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-sm font-medium text-gray-500">
                                Description
                              </dt>
                              <dd className="text-sm text-gray-900">
                                {selectedInstruction?.description}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-sm font-medium text-gray-500">
                                Status
                              </dt>
                              <dd>
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    selectedInstruction?.isActive
                                      ? "bg-green-100 text-green-800"
                                      : "bg-red-100 text-red-800"
                                  }`}
                                >
                                  {selectedInstruction?.isActive
                                    ? "Active"
                                    : "Inactive"}
                                </span>
                              </dd>
                            </div>
                          </dl>
                        </div>

                        <div>
                          <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wide">
                            Configuration
                          </h4>
                          <dl className="mt-2 space-y-2">
                            <div>
                              <dt className="text-sm font-medium text-gray-500">
                                Tone
                              </dt>
                              <dd className="text-sm text-gray-900">
                                {toneOptions.find(t => t.value === selectedInstruction?.tone)?.label || selectedInstruction?.tone}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-sm font-medium text-gray-500">
                                Style
                              </dt>
                              <dd className="text-sm text-gray-900">
                                {styleOptions.find(s => s.value === selectedInstruction?.style)?.label || selectedInstruction?.style}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-sm font-medium text-gray-500">
                                Allowed Topics
                              </dt>
                              <dd className="text-sm text-gray-900">
                                {selectedInstruction?.allowedTopics?.length || 0} topics
                              </dd>
                            </div>
                            <div>
                              <dt className="text-sm font-medium text-gray-500">
                                Restricted Keywords
                              </dt>
                              <dd className="text-sm text-gray-900">
                                {selectedInstruction?.restrictedKeywords?.length || 0} keywords
                              </dd>
                            </div>
                            <div>
                              <dt className="text-sm font-medium text-gray-500">
                                Last Modified
                              </dt>
                              <dd className="text-sm text-gray-900">
                                {selectedInstruction?.lastModified
                                  ? new Date(selectedInstruction.lastModified).toLocaleDateString()
                                  : "N/A"}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-sm font-medium text-gray-500">
                                Modified By
                              </dt>
                              <dd className="text-sm text-gray-900">
                                {selectedInstruction?.modifiedBy || "N/A"}
                              </dd>
                            </div>
                          </dl>
                        </div>
                      </div>

                      {/* System Prompt */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wide mb-2">
                          System Prompt
                        </h4>
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <pre className="text-sm text-gray-900 whitespace-pre-wrap">
                            {selectedInstruction?.systemPrompt}
                          </pre>
                        </div>
                      </div>

                      {/* Topics and Keywords */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wide mb-2">
                            Allowed Topics ({selectedInstruction?.allowedTopics?.length || 0})
                          </h4>
                          <div className="bg-gray-50 p-4 rounded-lg max-h-40 overflow-y-auto">
                            {selectedInstruction?.allowedTopics?.length ? (
                              selectedInstruction.allowedTopics.map((topic, index) => (
                                <span
                                  key={index}
                                  className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded mr-2 mb-2"
                                >
                                  {topic}
                                </span>
                              ))
                            ) : (
                              <p className="text-sm text-gray-500">No topics defined</p>
                            )}
                          </div>
                        </div>

                        <div>
                          <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wide mb-2">
                            Restricted Keywords ({selectedInstruction?.restrictedKeywords?.length || 0})
                          </h4>
                          <div className="bg-gray-50 p-4 rounded-lg max-h-40 overflow-y-auto">
                            {selectedInstruction?.restrictedKeywords?.length ? (
                              selectedInstruction.restrictedKeywords.map((keyword, index) => (
                                <span
                                  key={index}
                                  className="inline-block bg-red-100 text-red-800 text-xs px-2 py-1 rounded mr-2 mb-2"
                                >
                                  {keyword}
                                </span>
                              ))
                            ) : (
                              <p className="text-sm text-gray-500">No restrictions defined</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Fallback Responses */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wide mb-2">
                          Fallback Responses
                        </h4>
                        <div className="space-y-4">
                          {selectedInstruction?.fallbackResponses?.offTopic && (
                            <div>
                              <dt className="text-sm font-medium text-gray-500">
                                Off-Topic Response
                              </dt>
                              <dd className="text-sm text-gray-900 bg-gray-50 p-3 rounded">
                                {selectedInstruction.fallbackResponses.offTopic}
                              </dd>
                            </div>
                          )}
                          {selectedInstruction?.fallbackResponses?.generalInquiry && (
                            <div>
                              <dt className="text-sm font-medium text-gray-500">
                                General Inquiry Response
                              </dt>
                              <dd className="text-sm text-gray-900 bg-gray-50 p-3 rounded">
                                {selectedInstruction.fallbackResponses.generalInquiry}
                              </dd>
                            </div>
                          )}
                          {selectedInstruction?.fallbackResponses?.missingProductInfo && (
                            <div>
                              <dt className="text-sm font-medium text-gray-500">
                                Missing Information Response
                              </dt>
                              <dd className="text-sm text-gray-900 bg-gray-50 p-3 rounded">
                                {selectedInstruction.fallbackResponses.missingProductInfo}
                              </dd>
                            </div>
                          )}
                          {selectedInstruction?.fallbackResponses?.outOfScope && (
                            <div>
                              <dt className="text-sm font-medium text-gray-500">
                                Out of Scope Response
                              </dt>
                              <dd className="text-sm text-gray-900 bg-gray-50 p-3 rounded">
                                {selectedInstruction.fallbackResponses.outOfScope}
                              </dd>
                            </div>
                          )}
                          {!selectedInstruction?.fallbackResponses?.offTopic && 
                           !selectedInstruction?.fallbackResponses?.generalInquiry && 
                           !selectedInstruction?.fallbackResponses?.missingProductInfo && 
                           !selectedInstruction?.fallbackResponses?.outOfScope && (
                            <p className="text-sm text-gray-500">No fallback responses defined</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6 text-center">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100">
                    <svg
                      className="h-6 w-6 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No instruction selected
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Select an instruction from the list to view details, or create a new one to get started.
                  </p>
                  <div className="mt-6">
                    <button
                      onClick={handleCreateNew}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      <svg
                        className="-ml-1 mr-2 h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        />
                      </svg>
                      Create New Instruction
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminInstructions;